'use client'

// Custom-drawn circular plasmid map (pL0C_CDS_IPDC, 3744 bp).
// Replaces the lab screenshot in Section A's Precision Genetic Design
// key point. Slow continuous rotation (60s/cycle), pauses on hover.
// Features are colored arcs of the same circular backbone, drawn via
// stroke-dasharray + stroke-dashoffset so each segment can be styled
// independently without overlapping path computation.

import { useState } from 'react'

const CX = 160
const CY = 160
const R  = 120
const TWO_PI_R = 2 * Math.PI * R          // circumference ≈ 754

// Feature segments expressed as start/end angles (degrees, 0° = top).
// Lengths in degrees translate cleanly to dashlengths via TWO_PI_R/360.
type Feature = {
  id: string
  label: string
  position: string   // e.g. "0–200 bp"
  startDeg: number
  endDeg: number
  color: string
  labelAngleDeg: number  // where the leader line points outward
  weight?: number  // stroke-width override
}

const OLIVE = '#5B7005'
const LIME  = '#9FAF08'
const EARTH = '#513205'
const EARTH_60 = 'rgba(81,50,5,0.6)'

const FEATURES: Feature[] = [
  { id: 'ori',    label: 'ori',         position: '1–280 bp',     startDeg: 0,   endDeg: 28,  color: OLIVE,    labelAngleDeg: 14 },
  { id: 'pro',    label: 'P_BAD',       position: '290–470 bp',   startDeg: 32,  endDeg: 50,  color: LIME,     labelAngleDeg: 41 },
  { id: 'rbs',    label: 'RBS',         position: '480–510 bp',   startDeg: 52,  endDeg: 56,  color: '#3F4B05', labelAngleDeg: 54 },
  { id: 'cds',    label: 'IPDC',        position: '520–2520 bp',  startDeg: 60,  endDeg: 232, color: EARTH,    labelAngleDeg: 146 },
  { id: 'term',   label: 'rrnB T1',     position: '2540–2780 bp', startDeg: 236, endDeg: 260, color: OLIVE,    labelAngleDeg: 248 },
  { id: 'ampR',   label: 'AmpR',        position: '2840–3700 bp', startDeg: 270, endDeg: 354, color: EARTH_60, labelAngleDeg: 312 },
]

// Convert a feature to stroke-dasharray + stroke-dashoffset for use on
// a single <circle r=120>. Angles are 0=top, clockwise. Circle starts
// at 3 o'clock by default, so we rotate -90deg in transform.
function arcDash(startDeg: number, endDeg: number) {
  const arcLen = ((endDeg - startDeg) / 360) * TWO_PI_R
  const gap    = TWO_PI_R - arcLen
  // Negative offset shifts the dash forward by `startDeg` of the circle.
  const offset = -((startDeg / 360) * TWO_PI_R)
  return { dasharray: `${arcLen.toFixed(2)} ${gap.toFixed(2)}`, dashoffset: offset.toFixed(2) }
}

// Tick marks every 250bp around the perimeter (~15 ticks)
const TICK_COUNT = 15

// Compute where a label should sit + the leader line endpoint outside the ring
function polar(angleDeg: number, radius: number) {
  // Convert "0=top, clockwise" → standard polar (0=right, ccw)
  const a = (angleDeg - 90) * Math.PI / 180
  return { x: CX + radius * Math.cos(a), y: CY + radius * Math.sin(a) }
}

interface Props {
  caption?: string  // visible filename text in centre
}

export default function PlasmidSVG({ caption }: Props = {}) {
  const [paused, setPaused] = useState(false)

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '1 / 1',
        maxWidth: 480,
      }}
    >
      <style>{`
        @keyframes plasmidSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .pl-rotor {
          transform-origin: 50% 50%;
          animation: plasmidSpin 60s linear infinite;
        }
        .pl-rotor[data-paused="true"] { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .pl-rotor { animation: none; }
        }
      `}</style>

      <svg
        viewBox="0 0 320 320"
        style={{ width: '100%', height: '100%', display: 'block', overflow: 'visible' }}
        aria-label="Custom-designed expression plasmid pL0C_CDS_IPDC, 3744 base pairs"
      >
        {/* Rotating group — only the ring + features rotate; centre text stays still */}
        <g className="pl-rotor" data-paused={paused || undefined}>
          {/* Backbone ring (faint) */}
          <circle cx={CX} cy={CY} r={R} fill="none" stroke={`rgba(81,50,5,0.18)`} strokeWidth={2} />

          {/* Feature arcs (each is a stroked circle with custom dasharray) */}
          {FEATURES.map(f => {
            const { dasharray, dashoffset } = arcDash(f.startDeg, f.endDeg)
            return (
              <circle
                key={f.id}
                cx={CX} cy={CY} r={R}
                fill="none"
                stroke={f.color}
                strokeWidth={f.weight ?? 12}
                strokeLinecap="butt"
                strokeDasharray={dasharray}
                strokeDashoffset={dashoffset}
                transform={`rotate(-90 ${CX} ${CY})`}
              />
            )
          })}

          {/* Tick marks every 360/TICK_COUNT degrees */}
          {Array.from({ length: TICK_COUNT }).map((_, i) => {
            const angle = (i * 360) / TICK_COUNT
            const inner = polar(angle, R + 8)
            const outer = polar(angle, R + 14)
            return (
              <line
                key={i}
                x1={inner.x} y1={inner.y}
                x2={outer.x} y2={outer.y}
                stroke="rgba(81,50,5,0.35)"
                strokeWidth={1}
              />
            )
          })}

          {/* Feature labels with leader lines (rotate alongside ring) */}
          {FEATURES.map(f => {
            const inner = polar(f.labelAngleDeg, R + 4)
            const mid   = polar(f.labelAngleDeg, R + 22)
            const labelPos = polar(f.labelAngleDeg, R + 28)
            // Anchor label end-side based on which half of the circle it's on
            const isRightSide = f.labelAngleDeg > 0 && f.labelAngleDeg < 180
            return (
              <g key={`lbl-${f.id}`}>
                <line
                  x1={inner.x} y1={inner.y}
                  x2={mid.x}   y2={mid.y}
                  stroke={f.color}
                  strokeWidth={1}
                  opacity={0.7}
                />
                <text
                  x={labelPos.x}
                  y={labelPos.y}
                  textAnchor={isRightSide ? 'start' : 'end'}
                  dominantBaseline="middle"
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    fill: EARTH,
                  }}
                >
                  {f.label}
                </text>
                <text
                  x={labelPos.x}
                  y={labelPos.y + 12}
                  textAnchor={isRightSide ? 'start' : 'end'}
                  dominantBaseline="middle"
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 8.5,
                    letterSpacing: '0.04em',
                    fill: EARTH_60,
                  }}
                >
                  {f.position}
                </text>
              </g>
            )
          })}
        </g>

        {/* Centre — does not rotate */}
        <g>
          <text
            x={CX} y={CY - 6}
            textAnchor="middle"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 18,
              fontWeight: 500,
              letterSpacing: '-0.01em',
              fill: EARTH,
            }}
          >
            {caption ?? 'pL0C_CDS_IPDC'}
          </text>
          <text
            x={CX} y={CY + 14}
            textAnchor="middle"
            style={{
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              fontSize: 14,
              fill: OLIVE,
            }}
          >
            3 744 bp
          </text>
          <line x1={CX - 28} y1={CY + 26} x2={CX + 28} y2={CY + 26}
            stroke={`rgba(81,50,5,0.25)`} strokeWidth={1} />
          <text
            x={CX} y={CY + 40}
            textAnchor="middle"
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 8,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              fontWeight: 600,
              fill: EARTH_60,
            }}
          >
            BIOSOIL · UPV
          </text>
        </g>
      </svg>
    </div>
  )
}
